import React from 'react';
import type { UnitDefinition, SeatingUnit } from '@/types/seating-v2.types';
import { UnitRenderer } from '../UnitRenderer';

interface RowsArrangementProps {
  unit: UnitDefinition;
  units: SeatingUnit[];
  cols: number;
  spacing: number;
  aisles?: number;
  stagger?: boolean;
  selectedSeat: number | null;
  onSeatClick: (globalSeatNumber: number) => void;
  onSeatDrop: (globalSeatNumber: number, guestId: number) => void;
}

/**
 * Arranges units in stacked rows with optional aisles and row labels.
 * Used for: theater, classroom, amphitheater
 */
export const RowsArrangement: React.FC<RowsArrangementProps> = ({
  unit,
  units,
  cols,
  spacing,
  aisles = 0,
  stagger = false,
  selectedSeat,
  onSeatClick,
  onSeatDrop,
}) => {
  // Group units into rows of `cols`
  const rows: SeatingUnit[][] = [];
  for (let i = 0; i < units.length; i += cols) {
    rows.push(units.slice(i, i + cols));
  }

  // Calculate aisle positions (evenly distributed)
  const aislePositions: number[] = [];
  if (aisles > 0) {
    const section = Math.floor(cols / (aisles + 1));
    for (let a = 1; a <= aisles; a++) {
      aislePositions.push(a * section);
    }
  }

  return (
    <div className="flex flex-col items-center p-6" style={{ gap: spacing + 8 }}>
      {/* Stage marker for theater/amphitheater */}
      {unit.tableShape === 'none' && (
        <div className="w-3/4 max-w-md py-2 bg-amber-50 border-2 border-amber-200 rounded-lg text-center text-amber-700 font-semibold text-sm mb-2 shadow-sm">
          🎭 STAGE / FRONT
        </div>
      )}

      {rows.map((row, rowIdx) => (
        <div
          key={rowIdx}
          className="flex items-center justify-center"
          style={{
            gap: spacing,
            // Stagger: shift odd rows by half a unit width
            ...(stagger && rowIdx % 2 === 1
              ? { paddingLeft: unit.seatRadius * 2 + spacing / 2 }
              : {}),
          }}
        >
          {/* Row label */}
          <span className="text-xs text-slate-400 font-mono w-6 text-right mr-1 flex-shrink-0">
            {String.fromCharCode(65 + rowIdx)}
          </span>

          {row.map((u, colIdx) => (
            <React.Fragment key={u.unitIndex}>
              {/* Aisle gap */}
              {aislePositions.includes(colIdx) && (
                <div style={{ width: spacing * 3 }} />
              )}
              <UnitRenderer
                unitIndex={u.unitIndex}
                label={u.label}
                unit={unit}
                seats={u.seats}
                selectedSeat={selectedSeat}
                onSeatClick={onSeatClick}
                onSeatDrop={onSeatDrop}
              />
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
};

export default RowsArrangement;
